import { useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import posthog from "posthog-js";
import { supabase } from "@/integrations/supabase/client";
import { AuthContext } from "@/context/auth-context";
import type { User, Session } from "@supabase/supabase-js";
import type { Profile, Library, AccountType } from "@/types/database";
import { getDeviceSessionSnapshot } from "@/lib/device-session";
import { toast } from "@/components/ui/sonner";
import { SESSION_EXPIRED_EVENT } from "@/lib/invoke-with-session-retry";
import { flushOfflineReaderSyncQueue } from "@/lib/offline-reader-sync";
import { flushOfflineStudyQueue } from "@/lib/offline-study";
import {
  beginStandaloneOauthRedirect,
  isStandaloneApp,
  shouldUseManualStandaloneOauthRedirect,
} from "@/lib/pwa-auth";
import { buildSiteUrl } from "@/lib/site-url";
import {
  flushOfflineAccountQueue,
  getOfflineAccountEventName,
  getOfflineLibrary,
  getOfflineProfile,
  hydrateOfflineLibrary,
  hydrateOfflineProfile,
} from "@/lib/offline-account";

const OFFLINE_AUTH_USER_KEY = "athr.offline-auth-user";
const SUPABASE_AUTH_STORAGE_KEY_FRAGMENT = "-auth-token";

const isInvalidRefreshTokenError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const message = "message" in error && typeof error.message === "string" ? error.message.toLowerCase() : "";
  const code = "code" in error && typeof error.code === "string" ? error.code.toLowerCase() : "";
  const status = "status" in error ? Number(error.status) : NaN;
  return (
    code === "refresh_token_not_found" ||
    message.includes("refresh token not found") ||
    (!Number.isNaN(status) && status === 400 && message.includes("refresh"))
  );
};

const clearPersistedSupabaseSession = () => {
  if (typeof window === "undefined") return;
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key) continue;
      if (key.startsWith("sb-") && key.includes(SUPABASE_AUTH_STORAGE_KEY_FRAGMENT)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Ignore storage cleanup failures and continue through signed-out bootstrap.
  }
};

const readOfflineAuthUser = (): User | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(OFFLINE_AUTH_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as User | null;
    return parsed && typeof parsed.id === "string" ? parsed : null;
  } catch {
    return null;
  }
};

const writeOfflineAuthUser = (user: User | null) => {
  if (typeof window === "undefined") return;
  try {
    if (!user) {
      window.localStorage.removeItem(OFFLINE_AUTH_USER_KEY);
      return;
    }
    window.localStorage.setItem(OFFLINE_AUTH_USER_KEY, JSON.stringify(user));
  } catch {
    // Ignore storage failures and keep regular auth flow intact.
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [library, setLibrary] = useState<Library | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const sessionSyncRef = useRef<{ timer: number | null; fingerprint: string | null }>({
    timer: null,
    fingerprint: null,
  });
  const sessionWarningTimerRef = useRef<number | null>(null);
  const warnedExpiryRef = useRef<number | null>(null);
  const currentUserRef = useRef<string | null>(null);

  const stopSessionHeartbeat = useCallback(() => {
    if (sessionSyncRef.current.timer) {
      window.clearInterval(sessionSyncRef.current.timer);
      sessionSyncRef.current.timer = null;
    }
  }, []);

  const clearSessionExpiryWarning = useCallback(() => {
    if (sessionWarningTimerRef.current) {
      window.clearTimeout(sessionWarningTimerRef.current);
      sessionWarningTimerRef.current = null;
    }
  }, []);

  const scheduleSessionExpiryWarning = useCallback((nextSession: Session | null) => {
    clearSessionExpiryWarning();
    warnedExpiryRef.current = null;

    if (!nextSession?.expires_at) return;
    const expiresAtMs = nextSession.expires_at * 1000;
    const warnAtMs = expiresAtMs - 2 * 60 * 1000;
    const delay = warnAtMs - Date.now();

    const emitWarning = () => {
      if (warnedExpiryRef.current === expiresAtMs) return;
      warnedExpiryRef.current = expiresAtMs;
      toast.warning("Session expires soon", {
        description: "For security, your session will expire in about 2 minutes. Save changes now.",
      });
    };

    if (delay <= 0) {
      if (expiresAtMs > Date.now()) {
        emitWarning();
      }
      return;
    }

    sessionWarningTimerRef.current = window.setTimeout(() => {
      emitWarning();
    }, delay);
  }, [clearSessionExpiryWarning]);

  const clearLocalAuthState = useCallback(() => {
    stopSessionHeartbeat();
    clearSessionExpiryWarning();
    sessionSyncRef.current.fingerprint = null;
    currentUserRef.current = null;
    writeOfflineAuthUser(null);
    setUser(null);
    setSession(null);
    setProfile(null);
    setLibrary(null);
  }, [clearSessionExpiryWarning, stopSessionHeartbeat]);

  const downgradeExpiredPlan = useCallback(async (resolvedProfile: Profile) => {
    if (!resolvedProfile.plan_expires_at || !resolvedProfile.plan_id) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;

    const expiresAt = new Date(resolvedProfile.plan_expires_at).getTime();
    if (Number.isNaN(expiresAt) || expiresAt > Date.now()) return;

    const { data: freePlan } = await supabase
      .from("ai_plans")
      .select("id")
      .eq("name", "Free")
      .maybeSingle();

    if (!freePlan?.id) return;

    const { error } = await supabase
      .from("profiles")
      .update({
        plan_id: freePlan.id,
        plan_started_at: null,
        plan_expires_at: null,
      } as never)
      .eq("id", resolvedProfile.id);

    if (error) return;

    const nextProfile = {
      ...resolvedProfile,
      plan_id: freePlan.id,
      plan_started_at: null,
      plan_expires_at: null,
    };
    setProfile(nextProfile);
    hydrateOfflineProfile(nextProfile);
  }, []);

  const syncCurrentDeviceSession = useCallback(async (userId: string, options?: { allowRevokedRecovery?: boolean }) => {
    try {
      const snapshot = await getDeviceSessionSnapshot();
      sessionSyncRef.current.fingerprint = snapshot.fingerprint;

      const { data: currentRow } = await supabase
        .from("user_device_sessions" as never)
        .select("revoked_at")
        .eq("user_id", userId)
        .eq("fingerprint", snapshot.fingerprint)
        .maybeSingle();

      if ((currentRow as { revoked_at?: string | null } | null)?.revoked_at) {
        if (options?.allowRevokedRecovery) {
          await supabase
            .from("user_device_sessions" as never)
            .delete()
            .eq("user_id", userId)
            .eq("fingerprint", snapshot.fingerprint);
        } else {
        toast.error(
          "Your session was ended because your account was signed in on another device. Please sign in again to continue on this device.",
          { duration: 8000 },
        );
        await supabase
          .from("user_device_sessions" as never)
          .delete()
          .eq("user_id", userId)
          .eq("fingerprint", snapshot.fingerprint);
        await supabase.auth.signOut();
        return;
        }
      }

      await supabase
        .from("user_device_sessions" as never)
        .update({
          is_current: false,
          revoked_at: new Date().toISOString(),
        } as never)
        .eq("user_id", userId)
        .neq("fingerprint", snapshot.fingerprint)
        .is("revoked_at", null);

      await supabase.auth.signOut({ scope: "others" });

      const { error: upsertError } = await supabase
        .from("user_device_sessions" as never)
        .upsert({
          user_id: userId,
          fingerprint: snapshot.fingerprint,
          device_title: snapshot.title,
          location_label: snapshot.location,
          timezone: snapshot.timezone,
          ip_address: snapshot.ip_address,
          isp_name: snapshot.isp_name,
          browser_version: snapshot.browser_version,
          os_version: snapshot.os_version,
          device_type: snapshot.device_type,
          is_current: true,
          last_activity: new Date().toISOString(),
          revoked_at: null,
        } as never, { onConflict: "user_id,fingerprint" });

      if (upsertError) {
        return;
      }
    } catch {
      // Session sync failure should not block auth lifecycle.
    }
  }, []);

  const startSessionHeartbeat = useCallback((userId: string) => {
    stopSessionHeartbeat();
    void syncCurrentDeviceSession(userId, { allowRevokedRecovery: true });
    sessionSyncRef.current.timer = window.setInterval(() => {
      void syncCurrentDeviceSession(userId);
    }, 60_000);
  }, [stopSessionHeartbeat, syncCurrentDeviceSession]);

  const fetchUserData = useCallback(async (userId: string) => {
    const cachedProfile = getOfflineProfile(userId);
    const cachedLibrary = getOfflineLibrary(userId);

    if (cachedProfile) {
      setProfile(cachedProfile);
    }
    if (cachedLibrary) {
      setLibrary(cachedLibrary);
    }

    try {
      // 1. Fetch Profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        if (!cachedProfile) {
          setProfile(null);
        }
      } else {
        const resolvedProfile = profileData as unknown as Profile;
        setProfile(resolvedProfile);
        if (resolvedProfile) {
          hydrateOfflineProfile(resolvedProfile);
          void downgradeExpiredPlan(resolvedProfile);
        }
      }

      // 2. Fetch Library (if owner)
      const { data: libraryData, error: libraryError } = await supabase
        .from("libraries")
        .select("*")
        .eq("owner_id", userId)
        .maybeSingle();

      if (!libraryError) {
        const resolvedLibrary = libraryData as unknown as Library;
        setLibrary(resolvedLibrary);
        hydrateOfflineLibrary(userId, resolvedLibrary);
      } else {
        if (!cachedLibrary) {
          setLibrary(null);
        }
      }

    } catch (error) {
      console.error("Unexpected error:", error);
      if (!cachedProfile) {
        setProfile(null);
      }
      if (!cachedLibrary) {
        setLibrary(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [downgradeExpiredPlan]);

  const applyOfflineUserState = useCallback((cachedUser: User | null) => {
    setSession(null);
    setUser(cachedUser);
    stopSessionHeartbeat();
    clearSessionExpiryWarning();
    sessionSyncRef.current.fingerprint = null;
    currentUserRef.current = cachedUser?.id ?? null;

    if (cachedUser?.id) {
      setProfile(getOfflineProfile(cachedUser.id));
      setLibrary(getOfflineLibrary(cachedUser.id));
    } else {
      setProfile(null);
      setLibrary(null);
    }

    setIsLoading(false);
  }, [clearSessionExpiryWarning, stopSessionHeartbeat]);

  const applySessionState = useCallback(async (nextSession: Session | null) => {
    if (!nextSession) {
      const canUseOfflineAuth = typeof navigator !== "undefined" && !navigator.onLine;
      const cachedOfflineUser = canUseOfflineAuth ? readOfflineAuthUser() : null;
      if (cachedOfflineUser) {
        applyOfflineUserState(cachedOfflineUser);
        return;
      }
    }

    setSession(nextSession);
    scheduleSessionExpiryWarning(nextSession);
    setUser(nextSession?.user ?? null);

    if (nextSession?.user) {
      writeOfflineAuthUser(nextSession.user);
      
      const isNewUser = currentUserRef.current !== nextSession.user.id;
      currentUserRef.current = nextSession.user.id;

      // Only show full-screen loader and fetch data if it's a new user session
      if (isNewUser) {
        setIsLoading(true);
        void fetchUserData(nextSession.user.id);
      }
      
      startSessionHeartbeat(nextSession.user.id);
      return;
    }

    stopSessionHeartbeat();
    clearSessionExpiryWarning();
    sessionSyncRef.current.fingerprint = null;
    currentUserRef.current = null;
    writeOfflineAuthUser(null);
    setProfile(null);
    setLibrary(null);
    setIsLoading(false);
  }, [applyOfflineUserState, clearSessionExpiryWarning, fetchUserData, scheduleSessionExpiryWarning, startSessionHeartbeat, stopSessionHeartbeat]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        let recoveredSession = error ? null : data.session;

        if (!recoveredSession && navigator.onLine) {
          const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError && isInvalidRefreshTokenError(refreshError)) {
            clearPersistedSupabaseSession();
          }
          if (!refreshError) {
            recoveredSession = refreshed.session;
          }
        }

        if (!cancelled) {
          await applySessionState(recoveredSession);
        }
      } catch {
        if (!cancelled) {
          await applySessionState(null);
        }
      }
    };

    void bootstrapSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, nextSession) => {
        // Prevent redundant reloads if it's just a focus/visibility event (e.g. INITIAL_SESSION waking up)
        // We only proceed if a structural event occurred or the session was refreshed.
        const isStructuralEvent =
          event === "SIGNED_IN" || 
          event === "SIGNED_OUT" || 
          event === "TOKEN_REFRESHED" || 
          event === "USER_UPDATED";
        
        if (isStructuralEvent) {
          await applySessionState(nextSession);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      stopSessionHeartbeat();
      clearSessionExpiryWarning();
    };
  }, [applySessionState, clearSessionExpiryWarning, stopSessionHeartbeat]);

  useEffect(() => {
    const handleSessionExpired = async () => {
      stopSessionHeartbeat();
      clearSessionExpiryWarning();
      sessionSyncRef.current.fingerprint = null;
      writeOfflineAuthUser(null);
      await supabase.auth.signOut();
      clearLocalAuthState();
      toast.error("Session expired", {
        description: "Please sign in again.",
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [clearLocalAuthState, clearSessionExpiryWarning, stopSessionHeartbeat]);

  useEffect(() => {
    if (!user) return;
    // Activity tracking on focus/visibility removed to prevent aggressive re-renders
  }, [user]);



  useEffect(() => {
    if (!user?.id) return;

    // Initial flush on mount
    void flushOfflineReaderSyncQueue();
    void flushOfflineStudyQueue();
    void flushOfflineAccountQueue();

    const lastFlushRef = { time: Date.now() };

    const flushQueuedReaderData = () => {
      const now = Date.now();
      // Throttle: Only flush if it's been at least 1 minute since last flush
      if (now - lastFlushRef.time > 1000 * 60) {
        lastFlushRef.time = now;
        void flushOfflineReaderSyncQueue();
        void flushOfflineStudyQueue();
        void flushOfflineAccountQueue();
      }
    };

    window.addEventListener("online", flushQueuedReaderData);

    return () => {
      window.removeEventListener("online", flushQueuedReaderData);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const syncOfflineAccount = () => {
      const cachedProfile = getOfflineProfile(user.id);
      const cachedLibrary = getOfflineLibrary(user.id);
      if (cachedProfile) {
        setProfile(cachedProfile);
      }
      if (cachedLibrary || typeof navigator === "undefined" || !navigator.onLine) {
        setLibrary(cachedLibrary);
      }
    };

    syncOfflineAccount();
    const eventName = getOfflineAccountEventName();
    window.addEventListener(eventName, syncOfflineAccount);
    window.addEventListener("storage", syncOfflineAccount);

    return () => {
      window.removeEventListener(eventName, syncOfflineAccount);
      window.removeEventListener("storage", syncOfflineAccount);
    };
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error: error as Error | null };
  };

  const signUp = async (
    email: string,
    password: string,
    meta?: { full_name: string; account_type?: AccountType },
    options?: { emailRedirectTo?: string },
  ) => {
    const { error } = await supabase.auth.signUp({
      email, password, options: {
        data: {
          full_name: meta?.full_name,
          account_type: meta?.account_type || "student"
        },
        emailRedirectTo: options?.emailRedirectTo,
      },
    });
    return { error: error as Error | null };
  };

  const signInWithGoogle = async (nextPath = "/onboarding") => {
    const redirectTo = `${buildSiteUrl("/auth/callback")}?next=${encodeURIComponent(nextPath)}`;
    const isStandalone = isStandaloneApp();
    const useManualStandaloneRedirect = shouldUseManualStandaloneOauthRedirect();
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        skipBrowserRedirect: useManualStandaloneRedirect,
        queryParams: {
          prompt: "select_account",
        },
      },
    });

    if (!error && isStandalone && useManualStandaloneRedirect && data?.url) {
      beginStandaloneOauthRedirect(data.url);
    }

    return { error: error as Error | null };
  };

  const signOut = useCallback(async () => {
    try {
      if (user && sessionSyncRef.current.fingerprint) {
        await supabase
          .from("user_device_sessions" as never)
          .update({
            is_current: false,
            revoked_at: new Date().toISOString(),
            last_activity: new Date().toISOString(),
          } as never)
          .eq("user_id", user.id)
          .eq("fingerprint", sessionSyncRef.current.fingerprint);
      }
    } catch {
      // no-op
    }

    await supabase.auth.signOut();
    clearLocalAuthState();
  }, [clearLocalAuthState, user]);

  const signOutAllDevices = useCallback(async () => {
    if (!user) {
      await signOut();
      return;
    }

    try {
      await supabase
        .from("user_device_sessions" as never)
        .update({
          is_current: false,
          revoked_at: new Date().toISOString(),
          last_activity: new Date().toISOString(),
        } as never)
        .eq("user_id", user.id)
        .is("revoked_at", null);
    } catch {
      // Continue with global auth sign-out even if the device-session mirror fails.
    }

    await supabase.auth.signOut({ scope: "global" });
    clearLocalAuthState();
  }, [clearLocalAuthState, signOut, user]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  }, [user, fetchUserData]);

  const isAdmin = profile?.role === "admin";
  // Check account_type first to prevent flash, then fallback to library existence
  const isLibraryOwner = profile?.account_type === "library_owner" || !!library;
  const accountType = profile?.account_type || null;
  const isOnboarded = !!profile?.onboarding_completed;
  const isVerified = Boolean(user?.email_confirmed_at);
  const needsOnboarding = Boolean(user) && isVerified && !isOnboarded;
  const activationStage = !user
    ? "signed_out"
    : !isVerified
      ? "signed_in_unverified"
      : needsOnboarding
        ? "signed_in_needs_onboarding"
        : "ready";

  useEffect(() => {
    if (!import.meta.env.VITE_POSTHOG_KEY) return;

    if (user) {
      posthog.identify(user.id, {
        email: user.email ?? undefined,
      });
      return;
    }

    posthog.reset();
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, session, profile, library,
      isLoading, isAdmin, isLibraryOwner, accountType, isOnboarded, isVerified, needsOnboarding, activationStage,
      signIn, signUp, signInWithGoogle, signOut, signOutAllDevices, refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}
